import { addBreadcrumb, logger } from '@sentry/react';
import type { Deployment } from '@workspace/shared';

import i18n from '@/config/i18n/i18n';

import { openOfficeDialog } from './officeDialogManager';
import { awaitDialogOutcome } from './officeDialogMessaging';

/**
 * Connect-Mailbox credential acquisition via the Office Dialog API.
 *
 * The dialog starts on the add-in's own origin (`taskpane.html?msalConnect=1`),
 * runs MSAL's REDIRECT flow inside its own window (loginRedirect →
 * identity-provider legs → handleRedirectPromise back on our origin — see
 * `msalDialogHandler.ts`), then hands the Microsoft identity token back with
 * `messageParent`.
 *
 * Why not `loginPopup`: MSAL's popup monitor polls `popup.closed`, and any page
 * in the redirect chain that sends a Cross-Origin-Opener-Policy header severs
 * the opener's handle, making the popup read as closed while it is still on
 * screen. Federated tenants (AAD → customer IdP, e.g. Workspace ONE) hit this
 * deterministically: MSAL reports `user_cancelled` within seconds while the
 * user is still typing their password. The Office dialog channel does not
 * depend on window handles, so COOP cannot break it.
 */

// Federated sign-in can involve an external IdP leg, MFA, and a first-run
// consent screen — real users have been observed taking 90s+. Keep this well
// above the Clerk dialog's 3 minutes.
const MSAL_DIALOG_TIMEOUT_MS = 300_000; // 5 minutes

/**
 * Office dialog event codes delivered on DialogEventReceived.
 *
 * 12006 means "closed as far as the host can tell" — a real user close, OR a
 * handle the host lost when an identity provider severed it mid-sign-in. It is
 * ambiguous, and treated as such (MailboxConnectDialogLostError). Every other
 * code is a host/navigation failure and is definitively not a cancellation;
 * reporting those as one is the conflation that hid the federated COOP
 * incident.
 */
const DIALOG_EVENT_CLOSED = 12_006;

/** The user declined sign-in at the identity provider. */
export class MailboxConnectCancelledError extends Error {
  constructor() {
    super('Mailbox connection was cancelled before completion.');
    this.name = 'MailboxConnectCancelledError';
  }
}

/**
 * The host stopped tracking the dialog. Deliberately NOT a cancellation: event
 * 12006 means "this dialog is closed as far as I can tell", which the host also
 * reports when an identity provider's Cross-Origin-Opener-Policy header severs
 * its handle on a window the user is still signing into. Treating it as a
 * cancellation is what told federated users they had cancelled a sign-in they
 * were midway through completing.
 *
 * The caller resolves the ambiguity by asking the backend whether the link
 * completed anyway — see the ticket flow in msalDialogHandler.
 */
export class MailboxConnectDialogLostError extends Error {
  constructor() {
    super('Lost track of the mailbox sign-in window.');
    this.name = 'MailboxConnectDialogLostError';
  }
}

/** What the taskpane hands the dialog so it can finish the link on its own. */
export interface MailboxDialogRequest {
  /**
   * Which region to complete against — the dialog can't read the deployment
   * claim itself (its storage is partitioned away from the taskpane's).
   *
   * Deliberately the deployment name and NOT a URL: this value reaches the
   * dialog through a URL fragment anyone can craft, and the dialog posts a
   * Microsoft identity token to whatever it names. A name is validated against
   * a fixed set and resolved to one of our own hosts, so a crafted link can at
   * worst pick the wrong region of ours; a URL would let it pick an attacker's
   * server and walk away with a token that links mailboxes.
   */
  deployment: Deployment;
  loginHint?: string | undefined;
  /** Single-use ticket authorising the dialog to complete the link. */
  ticket: string;
}

/**
 * Connect Mailbox failed for a reason that is NOT user cancellation. Carries
 * the diagnostic context Sentry needs, since the failure is reported once from
 * `useConnectMailbox`'s onError rather than at each rejection site.
 */
export class MailboxConnectFailedError extends Error {
  /** Where the flow broke, for Sentry grouping. */
  readonly stage: 'dialog-display' | 'dialog-timeout' | 'provider';
  /** Office DialogEventReceived code, when the host reported one. */
  readonly officeDialogErrorCode: number | undefined;

  constructor(
    message: string,
    stage: MailboxConnectFailedError['stage'],
    options: { cause?: unknown; officeDialogErrorCode?: number | undefined } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'MailboxConnectFailedError';
    this.stage = stage;
    this.officeDialogErrorCode = options.officeDialogErrorCode;
  }
}

/** Result handed back by the connect dialog on success. */
export interface MailboxCredentialResult {
  accessToken: string;
  email: string | null;
}

/** True when the Office dialog surface is usable in this host. */
export const isOfficeDialogAvailable = (): boolean =>
  typeof Office !== 'undefined' && typeof Office?.context?.ui?.displayDialogAsync === 'function';

/**
 * Acquires a Microsoft identity token for Connect Mailbox through an Office
 * dialog, and hands the dialog what it needs to complete the link on its own.
 *
 * Resolves with the token and the authenticated account's email. Rejects with:
 *  - MailboxConnectDialogLostError — the host stopped tracking the window.
 *    Ambiguous: ask the backend whether the link completed before calling it a
 *    cancellation.
 *  - MailboxConnectCancelledError — the provider reported an explicit decline.
 *  - MailboxConnectFailedError — timeout, host failure, or a relayed error.
 *
 *  - DialogBlockedError — the host never opened the window (12009/12011).
 *
 * Everything handed over rides the URL fragment between our own windows, which
 * browsers never send to a server: the mailbox address stays out of access
 * logs and so does the ticket, which is a credential. (Inside the dialog the
 * ticket then rides MSAL's `state` through Microsoft — see ConnectTicketService
 * for why that exposure is accepted.)
 */
export async function acquireMailboxTokenViaDialog(
  request: MailboxDialogRequest,
): Promise<MailboxCredentialResult> {
  const url = new URL('/taskpane.html', window.location.origin);
  url.searchParams.set('msalConnect', '1');

  // Fragment, not query: browsers never send it to a server, keeping the
  // mailbox address out of access logs and the ticket — a credential — with it.
  const fragment = new URLSearchParams({
    deployment: request.deployment,
    ticket: request.ticket,
  });
  if (request.loginHint) {
    fragment.set('loginHint', request.loginHint);
  }
  url.hash = fragment.toString();

  addBreadcrumb({ message: '[MsalConnectDialog] opening Office connect dialog' });
  // Logged, not just breadcrumbed: a cancelled connect never captures an error
  // event, and breadcrumbs only ship attached to one — so without this the
  // whole attempt is invisible in Sentry.
  logger.info('Connect mailbox: opening Office dialog', {
    hasLoginHint: Boolean(request.loginHint),
  });
  const dialog = await openOfficeDialog(url.toString(), { height: 70, width: 40 });

  const outcome = await awaitDialogOutcome(dialog, {
    label: 'MsalConnectDialog',
    timeoutMs: MSAL_DIALOG_TIMEOUT_MS,
  });

  if (outcome.type === 'timeout') {
    // oxlint-disable-next-line no-console
    console.error(
      `[MsalConnectDialog] connect dialog timed out after ${MSAL_DIALOG_TIMEOUT_MS}ms without a token; ignored ${outcome.ignoredMessageCount} unrecognized message(s). The dialog never relayed a result — suspect a misrouted return leg (see msalDialogHandler).`,
    );
    throw new MailboxConnectFailedError(i18n.t('connectMailbox.error.timedOut'), 'dialog-timeout');
  }

  if (outcome.type === 'closed') {
    const { code } = outcome;

    if (code === DIALOG_EVENT_CLOSED) {
      // Ambiguous by design — see MailboxConnectDialogLostError. Either the
      // user closed the window, or an identity provider severed the host's
      // handle on a sign-in still in progress. The caller settles it against
      // the backend rather than guessing.
      logger.warn('Connect mailbox: host stopped tracking the sign-in window', {
        ignoredMessageCount: outcome.ignoredMessageCount,
        officeDialogErrorCode: code,
      });
      throw new MailboxConnectDialogLostError();
    }

    // 12002 (navigation failed), 12003 (non-HTTPS URL), 12004 (domain missing
    // from the manifest's AppDomains) and friends are host failures, not
    // cancellations — surface the code so a support screenshot is actionable.
    // oxlint-disable-next-line no-console
    console.error(
      `[MsalConnectDialog] Office dialog failed with event code ${code ?? 'unknown'} (not a user close). 12002/12004 usually mean the sign-in URL or the identity provider's domain is missing from the manifest AppDomains.`,
    );
    throw new MailboxConnectFailedError(
      i18n.t('connectMailbox.error.dialogFailed', { code: code ?? 'unknown' }),
      'dialog-display',
      { officeDialogErrorCode: code },
    );
  }

  const { message } = outcome;
  if (message.type === 'msal:token') {
    return { accessToken: message.accessToken, email: message.email };
  }
  if (message.type === 'auth:error') {
    // The dialog window already logged and captured the underlying cause; this
    // is the relayed, user-safe summary.
    // oxlint-disable-next-line no-console
    console.error('[MsalConnectDialog] connect dialog reported a sign-in failure:', message.error);
    throw new MailboxConnectFailedError(message.error, 'provider');
  }
  // auth:cancel or an unrelated success shape — the user did not sign in.
  logger.warn('Connect mailbox: dialog reported cancellation', { messageType: message.type });
  throw new MailboxConnectCancelledError();
}
